import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { usePostApiWorkspacesWorkspaceIdInvitations } from '@/api/generated/workspaces/workspaces';
import { useWorkspaceStore } from '@/stores/useWorkspaceStore';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';

const inviteSchema = z.object({
  email: z.string().email('E-mail inválido'),
  role: z.enum(['Admin', 'Agent', 'Owner']),
});

type InviteFormValues = z.infer<typeof inviteSchema>;

interface InviteMemberModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export const InviteMemberModal: React.FC<InviteMemberModalProps> = ({ open, onOpenChange, onSuccess }) => {
  const { activeWorkspaceId, currentRole } = useWorkspaceStore();
  const inviteMutation = usePostApiWorkspacesWorkspaceIdInvitations();

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<InviteFormValues>({
    resolver: zodResolver(inviteSchema),
    defaultValues: {
      role: 'Agent',
    },
  });

  const selectedRole = watch('role');

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      reset();
    }
    onOpenChange(newOpen);
  };

  const onSubmit = async (data: InviteFormValues) => {
    if (!activeWorkspaceId) return;

    try {
      await inviteMutation.mutateAsync({
        workspaceId: activeWorkspaceId,
        data: {
          email: data.email,
          role: data.role,
        },
      });
      toast.success('Convite enviado com sucesso!');
      if (onSuccess) onSuccess();
      handleOpenChange(false);
    } catch (error: any) {
      // The interceptor in client.ts catches TeamQuotaExceeded, CannotInviteOwner, etc.
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Convidar Membro</DialogTitle>
          <DialogDescription>
            Envie um convite para adicionar um novo membro ao seu workspace.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="email">E-mail</Label>
            <Input
              id="email"
              type="email"
              placeholder="exemplo@email.com"
              {...register('email')}
            />
            {errors.email && (
              <p className="text-sm text-destructive">{errors.email.message}</p>
            )}
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="role">Função</Label>
            <Select
              value={selectedRole}
              onValueChange={(val: 'Admin' | 'Agent' | 'Owner') => setValue('role', val)}
            >
              <SelectTrigger id="role">
                <SelectValue placeholder="Selecione uma função" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Agent">Agente</SelectItem>
                <SelectItem value="Admin">Administrador</SelectItem>
                {currentRole === 'Owner' && <SelectItem value="Owner">Dono</SelectItem>}
              </SelectContent>
            </Select>
            {errors.role && (
              <p className="text-sm text-destructive">{errors.role.message}</p>
            )}
            <p className="text-xs text-muted-foreground mt-1">
              {selectedRole === 'Agent' && 'Agentes podem apenas responder conversas.'}
              {selectedRole === 'Admin' && 'Administradores podem gerenciar lojas e a equipe.'}
              {selectedRole === 'Owner' && 'Donos têm acesso total, incluindo faturamento.'}
            </p>
          </div>
          
          <DialogFooter className="pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Enviando...' : 'Enviar Convite'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
